"use strict";
/* --------------------------------------------------------------------------------------------
 *  Licensed to the .NET Foundation under one or more agreements.
 *  The .NET Foundation licenses this file to you under the MIT license.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 * ------------------------------------------------------------------------------------------ */
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocalMemoryCacheSingleton = void 0;
const nodeCache = require("node-cache");
const EventStreamEvents_1 = require("./EventStream/EventStreamEvents");
const CommandExecutor_1 = require("./Utils/CommandExecutor");
const TypescriptUtilities_1 = require("./Utils/TypescriptUtilities");
class LocalMemoryCacheSingleton {
    constructor(timeToLiveMultiplier = 1) {
        this.timeToLiveMultiplier = timeToLiveMultiplier;
        this.cache = new nodeCache();
    }
    /**
     *
     * @param timeToLiveMultiplier The vscode setting that multiplies the time to live of the cache.
     * Allows users to turn off the cache if it causes problems or if its working well to increase the ttl for better performance.
     * Following the pattern of our other vscode settings, this is set initially and should be locked in during extension activation until that logic is decoupled.
     * It'd be best to not try to set this number again after the extension is activated; it will not work.
     *
     * @returns The instance of the singleton.
     */
    static getInstance(timeToLiveMultiplier = 1) {
        if (!LocalMemoryCacheSingleton.instance) {
            LocalMemoryCacheSingleton.instance = new LocalMemoryCacheSingleton(timeToLiveMultiplier);
        }
        return LocalMemoryCacheSingleton.instance;
    }
    /**
     * @returns the object in the cache with the key, key. undefined if the cache is empty. Telemetry will show this as a string undefined but it is undefined.
     */
    get(key, context) {
        var _a;
        const result = this.cache.get(key);
        context.eventStream.post(new EventStreamEvents_1.CacheGetEvent(`Checking the cache at ${new Date().toISOString()}`, key, (_a = JSON.stringify(result)) !== null && _a !== void 0 ? _a : 'undefined'));
        return result;
    }
    getCommand(key, context) {
        return this.get(this.cacheableCommandToKey(key), context);
    }
    /**
     *
     * @param metadata if ttl is 0, it won't be added to the cache.
     * @returns
     */
    put(key, obj, metadata, context) {
        metadata.ttlMs = metadata.ttlMs * this.timeToLiveMultiplier;
        if (metadata.ttlMs === 0) {
            context.eventStream.post(new EventStreamEvents_1.CachePutEvent(`TTL is 0 : Not to the cache at ${new Date().toISOString()}`, key, JSON.stringify(obj), metadata.ttlMs.toString()));
            return;
        }
        context.eventStream.post(new EventStreamEvents_1.CachePutEvent(`Adding to the cache at ${new Date().toISOString()}`, key, JSON.stringify(obj), metadata.ttlMs.toString()));
        this.cache.set(key, obj, metadata.ttlMs / 1000);
    }
    putCommand(key, obj, context) {
        var _a, _b;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const ttl = (_b = (_a = key.options) === null || _a === void 0 ? void 0 : _a.dotnetInstallToolCacheTtlMs) !== null && _b !== void 0 ? _b : 5000;
        return this.put(this.cacheableCommandToKey(key), obj, { ttlMs: ttl }, context);
    }
    invalidate(context) {
        context === null || context === void 0 ? void 0 : context.eventStream.post(new EventStreamEvents_1.CacheClearEvent(`Wiping the cache at ${new Date().toISOString()}`));
        this.cache.flushAll();
    }
    cacheableCommandToKey(key) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return `${CommandExecutor_1.CommandExecutor.prettifyCommandExecutorCommand(key.command)}${JSON.stringify(key.options, function replacer(k, v) {
            // Replace the dotnetInstallToolCacheTtlMs key with undefined so that it doesn't affect the cache key.
            if (k === 'dotnetInstallToolCacheTtlMs') {
                return undefined;
            }
            else if (k === 'env') {
                return `${(0, TypescriptUtilities_1.minimizeEnvironment)(v)}`;
            }
            return v;
        })}`;
    }
}
exports.LocalMemoryCacheSingleton = LocalMemoryCacheSingleton;
;
//# sourceMappingURL=LocalMemoryCacheSingleton.js.map